import { useState, useEffect, useCallback, useRef } from "react"
import { useApi } from "@/lib/api"
import { useAuth } from "@/contexts/auth-context"
import { REPORT_CONFIGS } from "@/types/report"
import type { ReportFilters } from "@/types/report"
import { getCached, setCache, invalidateCache } from "@/lib/report-cache"

function hashToken(t: string | null): string {
  if (!t) return "anon"
  let h = 0
  for (let i = 0; i < t.length; i++) h = ((h << 5) - h + t.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

export function useReportData(reportCode: string, filters: ReportFilters) {
  const { phoenixApi } = useApi()
  const { isAuthenticated, token } = useAuth()
  const [data, setData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const config = REPORT_CONFIGS.find((r) => r.code === reportCode)

  const cacheKeyRef = useRef("")

  const buildCacheKey = useCallback((code: string, f: ReportFilters) => {
    return `${hashToken(token)}::${code}::${JSON.stringify(f)}`
  }, [token])

  useEffect(() => {
    if (!isAuthenticated) invalidateCache()
  }, [isAuthenticated])

  const fetchData = useCallback(async (bypassCache = false) => {
    if (!config?.enabled || !isAuthenticated) return

    const key = buildCacheKey(reportCode, filters)
    cacheKeyRef.current = key

    if (!bypassCache) {
      const cached = getCached(key)
      if (cached) {
        setData(cached)
        setError(null)
        return
      }
    }

    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value != null && value !== "") params.set(key, String(value))
      })

      const result = await phoenixApi.get<any[]>(`${config.apiEndpoint}?${params.toString()}`)
      const resultData = result ?? []
      setData(resultData)
      setCache(key, resultData)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load report data")
      setData([])
    } finally {
      setIsLoading(false)
    }
  }, [reportCode, filters, isAuthenticated, config?.enabled, phoenixApi, buildCacheKey])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refresh = useCallback(() => {
    invalidateCache(cacheKeyRef.current)
    return fetchData(true)
  }, [fetchData])

  return { data, isLoading, error, refresh }
}
